import React, { useState } from 'react';
import { useLMS } from '../../context/LMSContext';

const CohortView = () => {
  const {
    currentUser,
    selectedCohortId,
    getCohortById,
    getModulesForCohort,
    getVisibleModulesForCohort,
    getUsersForCohort,
    toggleWeekVisibility,
    scheduleWeekVisibility,
    setCurrentView,
    setSelectedWeek,
    updateCohort
  } = useLMS();

  const [activeTab, setActiveTab] = useState('modules');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEditCohortModal, setShowEditCohortModal] = useState(false);
  const [selectedWeekForSchedule, setSelectedWeekForSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [editCohortData, setEditCohortData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    maxCapacity: 30
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = isAdmin || currentUser?.role === 'professor';
  const cohort = getCohortById(selectedCohortId);
  const modules = isStaff 
    ? getModulesForCohort(selectedCohortId) 
    : getVisibleModulesForCohort(selectedCohortId);
  const students = getUsersForCohort(selectedCohortId);

  if (!cohort) {
    return (
      <div className="cohort-view">
        <div className="empty-state">
          <h2>No cohort selected</h2>
          <p>Please select a cohort from the sidebar</p>
        </div>
      </div>
    );
  }

  // FIXED: Parse date string as local time to avoid timezone shift
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Split the date string and create date in local timezone
    // This prevents the "off by one day" issue caused by UTC interpretation
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Shorter date format for the meta display
  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleWeekClick = (weekNumber) => {
    setSelectedWeek(weekNumber);
    setCurrentView('module');
  };

  const handleToggleVisibility = (weekNumber, e) => {
    e.stopPropagation();
    toggleWeekVisibility(selectedCohortId, weekNumber);
  };

  const openScheduleModal = (weekNumber, e) => {
    e.stopPropagation();
    setSelectedWeekForSchedule(weekNumber);
    setScheduleDate('');
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = (e) => {
    e.preventDefault();
    if (selectedWeekForSchedule && scheduleDate) {
      scheduleWeekVisibility(selectedCohortId, selectedWeekForSchedule, scheduleDate);
      setShowScheduleModal(false);
    }
  };

  const openEditCohortModal = () => {
    setEditCohortData({
      name: cohort.name || '',
      startDate: cohort.startDate || cohort.start_date || '',
      endDate: cohort.endDate || cohort.end_date || '',
      maxCapacity: cohort.maxCapacity || cohort.max_capacity || 30
    });
    setShowEditCohortModal(true);
  };

  const handleEditCohort = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const result = await updateCohort(selectedCohortId, {
      name: editCohortData.name,
      start_date: editCohortData.startDate,
      end_date: editCohortData.endDate,
      max_capacity: editCohortData.maxCapacity
    });
    
    setIsSubmitting(false);
    
    if (result?.error) {
      alert(`Failed to update cohort: ${result.error}`);
      return;
    }
    
    setShowEditCohortModal(false);
  };

  return (
    <div className="cohort-view">
      {/* Cohort Header */}
      <div className="cohort-header">
        <div className="breadcrumb">
          <span onClick={() => setCurrentView('dashboard')}>Dashboard</span>
          <span className="separator">/</span>
          <span className="current">{cohort.name}</span>
        </div>
        
        <div className="header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>{cohort.name}</h1>
          {isStaff && (
            <button 
              className="btn-secondary"
              onClick={openEditCohortModal}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              ✏️ Edit Cohort
            </button>
          )}
        </div>
        
        <div className="cohort-meta">
          <span className={`status-badge ${cohort.status}`}>
            {cohort.status.charAt(0).toUpperCase() + cohort.status.slice(1)}
          </span>
          <span className="meta-item">
            📅 {formatDateShort(cohort.startDate || cohort.start_date)} - {formatDateShort(cohort.endDate || cohort.end_date)}
          </span>
          <span className="meta-item">
            👥 {cohort.enrolledCount || 0} students enrolled
          </span>
        </div>
        
        <div className="lecture-schedule">
          <span className="schedule-icon">🎓</span>
          <span>Live Lectures: Mon, Wed, Fri • 11:30 AM - 3:00 PM EST</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'modules' ? 'active' : ''}`}
          onClick={() => setActiveTab('modules')}
        >
          📚 Modules
        </button>
        {isStaff && (
          <button 
            className={`tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            👥 Students ({students.length})
          </button>
        )}
        <button 
          className={`tab ${activeTab === 'syllabus' ? 'active' : ''}`}
          onClick={() => { setActiveTab('syllabus'); setCurrentView('syllabus'); }}
        >
          📋 Syllabus & Attachments
        </button>
      </div>

      {/* Modules Tab */}
      {activeTab === 'modules' && (
        <div className="modules-list">
          {modules.map(module => (
            <div 
              key={module.weekNumber}
              className={`module-row ${!module.isVisible ? 'hidden-module' : ''}`}
              onClick={() => handleWeekClick(module.weekNumber)}
            >
              <div className="module-row-left">
                <div className="week-number">
                  <span>Week {module.weekNumber}</span>
                  <span className="modules-available">{(module.items || []).length} Items</span>
                </div>
                <div className="module-info">
                  <h3>{module.title}</h3>
                  <div className="module-meta">
                    {module.visibleDate && !module.isVisible && (
                      <span className="scheduled">
                        📅 Scheduled: {formatDateShort(module.visibleDate)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="module-row-right">
                {!module.isVisible ? (
                  <span className="visibility-badge hidden">🔒 Hidden</span>
                ) : (
                  <span className="visibility-badge visible">✅ Visible</span>
                )}
                
                {isStaff && (
                  <div className="module-actions">
                    <button 
                      className="btn-icon"
                      title={module.isVisible ? 'Hide' : 'Show'}
                      onClick={(e) => handleToggleVisibility(module.weekNumber, e)}
                    >
                      {module.isVisible ? '🙈' : '👁️'}
                    </button>
                    <button 
                      className="btn-icon"
                      title="Schedule"
                      onClick={(e) => openScheduleModal(module.weekNumber, e)}
                    >
                      📅
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && isStaff && (
        <div className="students-section">
          {students.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Enrolled</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.id}>
                    <td>
                      <div className="student-cell">
                        {student.avatar_url ? (
                          <img src={student.avatar_url} alt={student.name} className="avatar-img" />
                        ) : (
                          <div className="avatar">
                            {(student.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                        )}
                        {student.name || 'Unknown'}
                      </div>
                    </td>
                    <td>{student.email}</td>
                    <td>
                      <span className={`status-badge ${student.status || 'active'}`}>
                        {student.status || 'active'}
                      </span>
                    </td>
                    <td>{student.createdAt ? formatDateShort(student.createdAt.split('T')[0]) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <p>No students enrolled yet</p>
            </div>
          )}
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule Module Visibility</h3>
              <button className="modal-close" onClick={() => setShowScheduleModal(false)}>×</button>
            </div>
            
            <p className="modal-description">
              Set a date when Week {selectedWeekForSchedule} will automatically become visible.
            </p>
            
            <form onSubmit={handleScheduleSubmit}>
              <div className="form-group">
                <label>Visibility Date</label>
                <input 
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  required
                />
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Cohort Modal */}
      {showEditCohortModal && (
        <div className="modal-overlay" onClick={() => setShowEditCohortModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Cohort</h3>
              <button className="modal-close" onClick={() => setShowEditCohortModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleEditCohort}>
              <div className="form-group">
                <label>Cohort Name</label>
                <input 
                  type="text"
                  value={editCohortData.name}
                  onChange={(e) => setEditCohortData({...editCohortData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input 
                    type="date"
                    value={editCohortData.startDate}
                    onChange={(e) => setEditCohortData({...editCohortData, startDate: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>End Date</label>
                  <input 
                    type="date"
                    value={editCohortData.endDate}
                    onChange={(e) => setEditCohortData({...editCohortData, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Max Capacity</label>
                <input 
                  type="number"
                  min="1"
                  value={editCohortData.maxCapacity}
                  onChange={(e) => setEditCohortData({...editCohortData, maxCapacity: parseInt(e.target.value, 10)})}
                  required
                />
              </div>

              <div className="info-banner" style={{ marginTop: '16px' }}>
                <span>⚠️</span>
                <span>Changing dates will affect how module items appear on the calendar for this cohort.</span>
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditCohortModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CohortView;
